/**
 * Animated, collapsible sidebar. Expands/collapses with a spring; on mobile it
 * slides in over a backdrop. Active route gets a highlighted pill with a layout
 * animation that glides between items.
 */

import { AnimatePresence, motion } from 'framer-motion';
import {
  BarChart3,
  BellRing,
  ChevronLeft,
  CreditCard,
  FileText,
  Layers,
  LayoutDashboard,
  type LucideIcon,
  MessagesSquare,
  Package,
  Settings as SettingsIcon,
  ShieldCheck,
  TrendingUp,
} from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { useNeedsAttention, useProfile } from '../hooks/useApi';
import { hasPortfolioAccess, useStores } from '../hooks/usePortal';
import { cn } from '../lib/cn';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

const NAV: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/winning-products', label: 'Winning Products', icon: TrendingUp },
  { to: '/attention', label: 'Needs attention', icon: BellRing },
  { to: '/orders', label: 'Orders', icon: Package },
  { to: '/conversations', label: 'Conversations', icon: MessagesSquare },
  { to: '/templates', label: 'Templates', icon: FileText },
  { to: '/reports', label: 'Reports', icon: BarChart3 },
  { to: '/settings', label: 'Settings', icon: SettingsIcon },
  { to: '/billing', label: 'Plans & Billing', icon: CreditCard },
];

/** Templates only matter on the Cloud API path; QR sends free-form. */
const QR_HIDDEN = new Set(['/templates']);

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

export function Sidebar({ collapsed, onToggleCollapse, mobileOpen, onCloseMobile }: SidebarProps) {
  const width = collapsed ? 76 : 248;
  const { data: attention } = useNeedsAttention();
  const attentionCount = attention?.count ?? 0;
  const { data: profile } = useProfile();
  const { data: storesData } = useStores();
  const isQr = profile?.whatsappProvider === 'qr';
  const showPortfolio = hasPortfolioAccess(storesData?.stores);

  // "All stores" sits right after Dashboard, and only for multi-store plans.
  const nav = NAV.filter((item) => !(isQr && QR_HIDDEN.has(item.to))).flatMap((item) =>
    item.to === '/dashboard' && showPortfolio
      ? [item, { to: '/portfolio', label: 'All stores', icon: Layers }]
      : [item],
  );

  const content = (
    <div className="flex h-full flex-col">
      {/* Brand */}
      <div className="flex h-16 items-center gap-3 px-5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-sm">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <AnimatePresence>
          {!collapsed && (
            <motion.span
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -6 }}
              className="whitespace-nowrap text-base font-bold tracking-tight text-ink"
            >
              COD Shield
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-1 px-3 py-2">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/dashboard'}
            onClick={onCloseMobile}
            className={({ isActive }) =>
              cn(
                'group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                isActive
                  ? 'text-brand-700 dark:text-brand-300'
                  : 'text-ink-muted hover:bg-surface-subtle hover:text-ink',
              )
            }
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <motion.span
                    layoutId="nav-active"
                    transition={{ type: 'spring', stiffness: 500, damping: 38 }}
                    className="absolute inset-0 rounded-xl bg-brand-50 dark:bg-brand-500/15"
                  />
                )}
                <item.icon className="relative z-10 h-5 w-5 shrink-0" strokeWidth={2} />
                {!collapsed && (
                  <span className="relative z-10 whitespace-nowrap">{item.label}</span>
                )}
                {item.to === '/attention' && attentionCount > 0 ? (
                  <span
                    className={cn(
                      'relative z-10 ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-xs font-semibold text-white',
                      collapsed && 'absolute right-1 top-1 ml-0',
                    )}
                  >
                    {attentionCount > 99 ? '99+' : attentionCount}
                  </span>
                ) : null}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Collapse toggle (desktop) */}
      <button
        type="button"
        onClick={onToggleCollapse}
        className="m-3 hidden items-center justify-center rounded-xl border border-line py-2 text-ink-subtle transition-colors hover:bg-surface-subtle hover:text-ink lg:flex"
      >
        <ChevronLeft className={cn('h-4 w-4 transition-transform', collapsed && 'rotate-180')} />
      </button>
    </div>
  );

  return (
    <>
      {/* Desktop */}
      <motion.aside
        animate={{ width }}
        transition={{ type: 'spring', stiffness: 400, damping: 40 }}
        className="sticky top-0 hidden h-screen shrink-0 border-r border-line bg-surface lg:block"
      >
        {content}
      </motion.aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onCloseMobile}
              className="fixed inset-0 z-40 bg-black/40 lg:hidden"
            />
            <motion.aside
              initial={{ x: -260 }}
              animate={{ x: 0 }}
              exit={{ x: -260 }}
              transition={{ type: 'spring', stiffness: 400, damping: 40 }}
              className="fixed inset-y-0 left-0 z-50 w-64 border-r border-line bg-surface lg:hidden"
            >
              {content}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
